import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiHome, 
  FiTrendingUp, 
  FiUsers, 
  FiPackage, 
  FiBarChart3, 
  FiX,
  FiActivity,
  FiChevronLeft,
  FiChevronRight
} = FiIcons;

const Sidebar = ({ isOpen, onClose, isCollapsed, toggleCollapse, user }) => {
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FiHome,
      description: 'Overview & KPIs'
    },
    {
      name: 'Products',
      href: '/product/abiraterone-acetate',
      icon: FiPackage,
      description: 'Product Analytics'
    },
    {
      name: 'Customers',
      href: '/customer/redoak-sourcing',
      icon: FiUsers,
      description: 'Customer Intelligence'
    },
    {
      name: 'IQVIA Intelligence',
      href: '/iqvia',
      icon: FiBarChart3,
      description: 'Market Data'
    }
  ];

  return (
    <>
      <div
        className={`fixed left-0 top-0 h-full bg-gradient-to-b from-slate-900 to-slate-800 z-50 transition-all duration-300 ease-in-out shadow-xl
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-6 border-b border-slate-700 h-20`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiActivity} className="text-white text-xl" />
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden whitespace-nowrap">
                <h1 className="text-white font-bold text-lg">Novadoz</h1>
                <p className="text-slate-400 text-xs">Pharma Analytics</p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* User Info */}
        <div className={`p-6 border-b border-slate-700 ${isCollapsed ? 'flex justify-center' : ''}`}>
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar}
              alt={user?.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-slate-600"
            />
            {!isCollapsed && (
              <div className="overflow-hidden whitespace-nowrap">
                <p className="text-white font-medium text-sm truncate max-w-[120px]">{user?.name}</p>
                <p className="text-slate-400 text-xs truncate max-w-[120px]">{user?.role}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.href || 
                              (item.href.includes('/product/') && location.pathname.startsWith('/product/')) ||
                              (item.href.includes('/customer/') && location.pathname.startsWith('/customer/'));
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative
                    ${isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                  title={isCollapsed ? item.name : ''}
                >
                  <SafeIcon 
                    icon={item.icon} 
                    className={`text-lg flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} 
                  />
                  {!isCollapsed && (
                    <div className="overflow-hidden whitespace-nowrap">
                      <p className="font-medium">{item.name}</p>
                      <p className={`text-xs ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>
                        {item.description}
                      </p>
                    </div>
                  )}
                  
                  {/* Tooltip for collapsed mode */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-4 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-lg border border-slate-700">
                      {item.name}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Collapse Toggle Button (Desktop Only) */}
        <button
          onClick={toggleCollapse}
          className="hidden lg:flex absolute -right-3 top-24 w-6 h-6 bg-slate-700 border border-slate-600 rounded-full items-center justify-center text-slate-300 hover:bg-blue-600 hover:text-white hover:border-blue-500 transition-all shadow-lg z-50"
        >
          <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-xs" />
        </button>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className={`bg-slate-800 rounded-lg p-4 border border-slate-700 ${isCollapsed ? 'flex justify-center items-center' : ''}`}>
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiTrendingUp} className="text-green-400" />
                  <span className="text-white text-sm font-medium">System Status</span>
                </div>
                <p className="text-slate-400 text-xs">All systems operational</p>
                <div className="flex items-center gap-1 mt-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-xs">Live Data</span>
                </div>
              </>
            ) : (
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" title="Systems Operational"></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;